import { useEffect, useRef, useState } from "react";
import { addressService } from "../../Api/Services/addressService";
import { postalCodeService } from "../../Api/Services/postalCodeService";
import { Input } from "../Input/Input";
import { formatPostalCode, unformat } from "../../Utils/DataFormatters";
import { Address } from "../../Models/Address";
import { FaChevronLeft } from "react-icons/fa";
import { toast } from "react-toastify";
import Button from "../Button/Button";

type AddressFormProps = {
  isEditing?: boolean;
  editedAddress?: Address | null;
  existingAddresses?: Address[];
  onClose: () => void;
  onSave: (createdAddressId?: string | null) => void;
};

const AddressForm = ({
  isEditing,
  editedAddress,
  existingAddresses,
  onClose,
  onSave
}: AddressFormProps) => {
  const [formData, setFormData] = useState({
    postalCode: "",
    street: "",
    number: "",
    complement: "",
    neighborhood: "",
    city: "",
    state: "",
    cityIbgeCode: ""
  });
  const [isPostalLookupLoading, setIsPostalLookupLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const postalLookupRef = useRef(0);

  useEffect(() => {
    if (isEditing && editedAddress) {
      setFormData({
        postalCode: formatPostalCode(editedAddress.postalCode || ""),
        street: editedAddress.street || "",
        number: editedAddress.number || "",
        complement: editedAddress.complement || "",
        neighborhood: editedAddress.neighborhood || "",
        city: editedAddress.city || "",
        state: editedAddress.state || "",
        cityIbgeCode: editedAddress.cityIbgeCode || ""
      });
    }
  }, [isEditing, editedAddress]);

  const handlePostalCodeChange = async (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const input = e.target.value;
    const formattedPostalCode = formatPostalCode(input);
    const numericPostalCode = unformat(formattedPostalCode);

    setFormData(prev => ({ 
      ...prev, 
      postalCode: formattedPostalCode
    }));

    if (numericPostalCode.length === 0) {
      setFormData(prev => ({
        ...prev,
        street: "",
        neighborhood: "",
        city: "",
        state: "",
        cityIbgeCode: ""
      }));
      return;
    }

    if (numericPostalCode.length === 8) {
      const lookupId = ++postalLookupRef.current;
      setIsPostalLookupLoading(true);
      try {
        const data = await postalCodeService.getAddressByPostalCode(numericPostalCode);
        if (lookupId !== postalLookupRef.current) {
          return;
        }
        if (!data.hasError) {
          setFormData(prev => ({
            ...prev,
            street: data.street,
            neighborhood: data.neighborhood,
            city: data.city,
            state: data.state,
            cityIbgeCode: data.cityIbgeCode
          }));
        }
      } catch (error: any) {
        if (lookupId === postalLookupRef.current) {
          const message =
            error.response?.data?.message ||
            "Ocorreu um erro ao buscar pelo CEP inserido, tente novamente mais tarde.";
          toast.error(message);
        }
      } finally {
        if (lookupId === postalLookupRef.current) {
          setIsPostalLookupLoading(false);
        }
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSaving) {
      return;
    }

    if (!formData.postalCode?.trim()) {
      toast.error("O CEP é obrigatório.");
      return;
    }

    if (!formData.number?.trim()) {
      toast.error("O número do endereço é obrigatório.");
      return;
    }

    if (!isEditing && !editedAddress && existingAddresses?.length) {
      const normalizedPostalCode = unformat(formData.postalCode);
      const hasDuplicatePostalCode = existingAddresses.some(
        address => unformat(address.postalCode) === normalizedPostalCode
      );
      if (hasDuplicatePostalCode) {
        toast.error("Já existe um endereço cadastrado com este CEP.");
        return;
      }
    }

    setIsSaving(true);
    try {
      if (!isEditing && !editedAddress) {
        const created = await addressService.create({
          street: formData.street,
          neighborhood: formData.neighborhood,
          number: formData.number,
          city: formData.city,
          state: formData.state,
          cityIbgeCode: formData.cityIbgeCode,
          postalCode: unformat(formData.postalCode),
          complement: formData.complement
        });

        if (created) {
          toast.success("Endereço criado com sucesso!");
        }

        onSave(created);
        return;
      }

      const updated = await addressService.update({
        id: editedAddress?.id!,
        number: formData.number,
        complement: formData.complement
      });

      if (updated) {
        toast.success("Endereço atualizado com sucesso!");
      }

      onSave();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center text-xs font-bold gap-2">
        <FaChevronLeft
          className="text-primary-600"
          onClick={onClose}
        />
        <p>{isEditing ? "EDITAR ENDEREÇO" : "CADASTRAR NOVO ENDEREÇO"}</p>
      </div>
      <form className="space-y-4">
        <Input
          label={"CEP"}
          type="text"
          name="postalCode"
          value={formData.postalCode}
          onChange={handlePostalCodeChange}
          required
          readOnly={isEditing}
          disabled={isPostalLookupLoading || isSaving}
          rightIcon={
            isPostalLookupLoading ? (
              <span className="inline-flex items-center">
                <span className="sr-only">{"Buscando endereço"}</span>
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-primary-600" />
              </span>
            ) : null
          }
        />

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <div className="col-span-2 sm:col-span-3">
            <Input
              label={"Logradouro"}
              type="text"
              name="street"
              value={formData.street}
              readOnly
              disabled={isPostalLookupLoading || isSaving}
            />
          </div>
          <div className="col-span-1">
            <Input
              label={"Número"}
              type="text"
              name="number"
              value={formData.number}
              onChange={e => setFormData({ ...formData, number: e.target.value })}
              required
              disabled={isPostalLookupLoading || isSaving}
            />
          </div>
          <div className="col-span-1 sm:col-span-4">
            <Input
              label={"Complemento"}
              type="text"
              name="complement"
              value={formData.complement}
              onChange={e => setFormData({ ...formData, complement: e.target.value })}
              disabled={isPostalLookupLoading || isSaving}
            />
          </div>
        </div>

        <Input
          label={"Bairro"}
          type="text"
          name="neighborhood"
          value={formData.neighborhood}
          readOnly
          disabled={isPostalLookupLoading || isSaving}
        />

        <div className="flex gap-2">
          <div className="flex-[3]">
            <Input
              label={"Cidade"}
              type="text"
              name="city"
              value={formData.city}
              readOnly
              disabled={isPostalLookupLoading || isSaving}
            />
          </div>
          <div className="flex-[1]">
            <Input
              label={"UF"}
              type="text"
              name="state"
              value={formData.state}
              readOnly
              disabled={isPostalLookupLoading || isSaving}
            />
          </div>
        </div>
      </form>
      <p className="text-xs text-gray-500">{"(*) Só é possível editar Número e Complemento."}</p>
      <div className="flex gap-2 mb-2">
        <Button variant="secondary"
          type="button"
          className="flex-1 py-2"
          onClick={onClose}
          disabled={isSaving}
        >
          {"Cancelar"}
        </Button>
        <Button isLoading={isSaving}
          className="flex-1 py-2"
          onClick={handleSubmit}
          disabled={isPostalLookupLoading || isSaving}
        >
          {"Salvar endereço"}
        </Button>
      </div>
    </div>
  );
};

export default AddressForm;
